import cn from 'classnames';
import { FC, MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { Button, Checkbox, Icon, InputLabel } from '@/shared/ui';

import { EncounterSchema } from '@/entities/templates';

import { templateEditorMessages } from '../../config';
import { useDismissableMenu } from '../../hooks';

import styles from './TemplateSpecialtiesSelect.module.scss';

type TemplateSpecialtiesSelectProps = {
  options: EncounterSchema[];
  value: string[];
  className?: string;
  disabled?: boolean;
  onChange: (schemaIds: string[]) => void;
};

export const TemplateSpecialtiesSelect: FC<TemplateSpecialtiesSelectProps> = (
  props,
) => {
  const { options, value, className, disabled, onChange } = props;

  const { formatMessage } = useIntl();

  const { containerRef, isOpen, setIsOpen } =
    useDismissableMenu<HTMLDivElement>();

  const selectedOptions = options.filter((option) => value.includes(option.id));
  const placeholder = formatMessage(
    templateEditorMessages.specialtiesPlaceholder,
  );

  const toggle = (schemaId: string) => {
    onChange(
      value.includes(schemaId)
        ? value.filter((id) => id !== schemaId)
        : [...value, schemaId],
    );
  };

  const toggleMenu = () => {
    if (!disabled) {
      setIsOpen((open) => !open);
    }
  };

  const handleFieldClick = (event: MouseEvent<HTMLDivElement>) => {
    // The opener and the chip remove buttons handle their own clicks; only
    // the empty area of the field opens the menu here.
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    toggleMenu();
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-component="template-specialties-select"
    >
      <InputLabel label={formatMessage(templateEditorMessages.specialties)} />
      <div ref={containerRef} className={styles.anchor}>
        <div
          className={cn(
            styles.field,
            isOpen && styles.fieldOpen,
            disabled && styles.disabled,
          )}
          onClick={handleFieldClick}
        >
          {selectedOptions.map((option) => (
            <span key={option.id} className={cn('p4b', styles.chip)}>
              {option.name}
              <Button
                variant="tertiary"
                size="tiny"
                icon="close"
                circle
                className={styles.chipRemove}
                title={formatMessage(templateEditorMessages.removeSpecialty)}
                disabled={disabled}
                onClick={() => toggle(option.id)}
              />
            </span>
          ))}
          <button
            type="button"
            className={styles.opener}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={placeholder}
            onClick={toggleMenu}
          >
            {selectedOptions.length === 0 && (
              <span className={cn('p3', styles.placeholder)}>
                {placeholder}
              </span>
            )}
            <Icon name="arrowDown" size={18} className={styles.arrow} />
          </button>
        </div>
        {isOpen && (
          <div className={styles.menu}>
            {options.length === 0 ? (
              <span className={cn('p4', styles.menuEmpty)}>
                {formatMessage(templateEditorMessages.noSpecialtiesAvailable)}
              </span>
            ) : (
              options.map((option) => (
                <Checkbox
                  key={option.id}
                  className={styles.menuItem}
                  checked={value.includes(option.id)}
                  onChange={() => toggle(option.id)}
                >
                  <span className={cn('p3', styles.menuItemName)}>
                    {option.name}
                  </span>
                </Checkbox>
              ))
            )}
          </div>
        )}
      </div>
      <span className={cn('p4', styles.hint)}>
        {formatMessage(templateEditorMessages.specialtiesHint)}
      </span>
    </div>
  );
};
